import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Sparkles, FileText, ChevronLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AiVoucherCreator from './sections/AiVoucher/AiVoucherCreator';
import ManualVoucherForm from './sections/ManualVoucher/ManualVoucherForm';
import PageContainer from '../../../../components/layout/PageContainer';
import MainLayout from '../../../../Layout/MainLayout';
import { useAgent } from '../../../../context/agent/AgentContext';
import { useDashboard } from '../../../../context/dashboard/DashboardContext';
import AiAnalysisProgress from './sections/AiVoucher/AiAnalysisProgress';

const CreateVoucherPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { analysisState, setAnalysisMinimized, resetAnalysis } = useDashboard();
    const [activeTab, setActiveTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return tabParam || location.state?.activeTab || (location.state?.initialData ? 'manual' : 'ai');
    });
    const [manualInitialData, setManualInitialData] = useState(() => {
        if (location.state?.initialData) {
            return location.state.initialData;
        }
        const saved = localStorage.getItem('trrip_voucher_draft');
        return saved ? JSON.parse(saved) : null;
    });

    const handleDataChange = useCallback((data) => {
        setManualInitialData(data);
    }, []);

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        navigate(`/create-voucher?tab=${newTab}`, {
            replace: true,
            state: {
                ...location.state,
                activeTab: newTab,
                initialData: manualInitialData
            }
        });
    };

    useEffect(() => {
        // Clear stale booking ID if there is no active draft
        const savedDraft = localStorage.getItem('trrip_voucher_draft');
        if (!savedDraft && !location.state?.initialData) {
            localStorage.removeItem('trrip_voucher_booking_id');
        }
    }, [location.state?.initialData]);


    useEffect(() => {
        const currentTabParam = searchParams.get('tab');
        if (currentTabParam) {
            setActiveTab(currentTabParam);
        } else if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        } else if (location.state?.initialData) {
            setActiveTab('manual');
        }
        if (location.state?.initialData) {
            setManualInitialData(location.state.initialData);
        }
    }, [location.state, searchParams]);

    const { voucherTheme } = useAgent();
    const [currentVoucherTheme, setCurrentVoucherTheme] = useState(() => voucherTheme || { isCustom: false, primaryColor: '#0d9488', secondaryColor: '#1d4ed8' });

    useEffect(() => {
        if (location.state?.customTheme) {
            setCurrentVoucherTheme(location.state.customTheme);
        } else if (voucherTheme) {
            setCurrentVoucherTheme(voucherTheme);
        }
    }, [location.state, voucherTheme]);

    const [pendingNavigate, setPendingNavigate] = useState(null);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [isFormSaved, setIsFormSaved] = useState(false);

    const handleBackClick = () => {
        if (isFormSaved) {
            navigate('/voucher');
            return;
        }
        if (manualInitialData || activeTab === 'manual') {
            setShowExitConfirm(true);
            setPendingNavigate({ path: '/voucher', state: {} });
        } else {
            navigate('/voucher');
        }
    };

    const confirmSaveDraft = () => {
        if (manualInitialData) {
            localStorage.setItem('trrip_voucher_draft', JSON.stringify(manualInitialData));
        }
        setShowExitConfirm(false);
        if (pendingNavigate) navigate(pendingNavigate.path, { state: pendingNavigate.state });
    };

    const confirmDiscard = () => {
        localStorage.removeItem('trrip_voucher_draft');
        localStorage.removeItem('trrip_voucher_booking_id');
        setShowExitConfirm(false);
        if (pendingNavigate) navigate(pendingNavigate.path, { state: pendingNavigate.state });
    };

    const handleSidebarTabChange = (tabId) => {
        if (tabId === 'create-voucher') return;

        if (isFormSaved) {
            navigate('/voucher', { state: { activeTab: tabId } });
            return;
        }

        if (manualInitialData || activeTab === 'manual') {
            setShowExitConfirm(true);
            setPendingNavigate({ path: '/voucher', state: { activeTab: tabId } });
        } else {
            navigate('/voucher', { state: { activeTab: tabId } });
        }
    };

    const [externalDroppedFiles, setExternalDroppedFiles] = useState([]);
    const [isDraggingOverPage, setIsDraggingOverPage] = useState(false);

    const preloadedHotelFiles = location.state?.preloadedHotelFile
        ? [location.state.preloadedHotelFile]
        : (location.state?.initialHotelFiles || location.state?.initialFiles || location.state?.files || []);
    const preloadedTravelFiles = location.state?.initialTravelFiles || [];
    const preloadedItineraryFiles = location.state?.initialItineraryFiles || [];

    const handleGlobalDragOver = (e) => {
        e.preventDefault();
        setIsDraggingOverPage(true);
    };

    const handleGlobalDragLeave = (e) => {
        e.preventDefault();
        if (e.relatedTarget === null) {
            setIsDraggingOverPage(false);
        }
    };

    const handleGlobalDrop = (e) => {
        e.preventDefault();
        setIsDraggingOverPage(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(file =>
            file.type === 'application/pdf' ||
            file.type.startsWith('image/')
        );

        if (validFiles.length > 0) {
            setActiveTab('ai');
            setExternalDroppedFiles(prev => {
                const existingNames = new Set(prev.map(f => f.name + f.size));
                const newFiles = validFiles.filter(f => !existingNames.has(f.name + f.size));
                return [...prev, ...newFiles];
            });
        }
    };

    return (
        <MainLayout activeTab="create-voucher" setActiveTab={handleSidebarTabChange}>
            <div
                className={`transition-all duration-500 ${isDraggingOverPage ? 'bg-teal-50/30' : ''}`}
                onDragOver={handleGlobalDragOver}
                onDragLeave={handleGlobalDragLeave}
                onDrop={handleGlobalDrop}
            >
                {/* Global File Drop Indicator */}
                {isDraggingOverPage && (
                    <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center bg-teal-500/5 backdrop-blur-sm border-2 border-teal-500/20 border-dashed m-6 rounded-[2.5rem]">
                        <div className="bg-white/90 backdrop-blur-md p-10 rounded-[2rem] shadow-2xl border border-teal-100 flex flex-col items-center animate-in zoom-in duration-300">
                            <div className="w-20 h-20 bg-teal-50 rounded-3xl flex items-center justify-center mb-6 shadow-sm ring-1 ring-teal-100">
                                <Sparkles size={40} className="text-teal-600 animate-pulse" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Drop your files here</h2>
                            <p className="text-gray-500 mt-2 text-center max-w-xs font-medium">We'll automatically extract the details to create your voucher</p>
                        </div>
                    </div>
                )}

                <PageContainer className="min-h-[calc(100vh-100px)] flex flex-col justify-between">
                    {/* Back Button */}
                    <div className="mb-2 flex items-center justify-between">
                        <button
                            onClick={handleBackClick}
                            className="flex items-center gap-1.5 text-slate-400 hover:text-teal-600 font-bold transition-all group px-2 py-1 rounded-lg hover:bg-teal-50/50 cursor-pointer"
                        >
                            <ChevronLeft size={18} className="transition-transform group-hover:-translate-x-0.5" />
                            <span className="text-sm">Back to Vouchers</span>
                        </button>
                    </div>

                    {/* Sleeker Header */}
                    <div className="mb-4 sm:mb-6">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6">
                            <div className="space-y-1 sm:space-y-2">
                                <h1 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-[#067978] tracking-tight leading-snug sm:leading-tight">
                                    Create voucher in <span className="text-teal-600 underline underline-offset-4 sm:underline-offset-8 decoration-teal-200">3 clicks.</span>
                                </h1>
                                <p className="text-slate-500 font-medium text-xs sm:text-sm md:text-lg max-w-2xl">
                                    Extract details automatically from your documents or fill them manually.
                                </p>
                            </div>

                            {/* Ultra-sleek Tabs */}
                            <div className="flex bg-slate-100/80 backdrop-blur-sm p-1 sm:p-1.5 rounded-xl sm:rounded-2xl border border-slate-200/50 shadow-sm w-full sm:w-auto self-stretch sm:self-start">
                                <button
                                    onClick={() => handleTabChange('ai')}
                                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer ${activeTab === 'ai'
                                        ? 'bg-white text-teal-700 shadow-md shadow-teal-500/5 translate-y-[-1px]'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
                                        }`}
                                >
                                    <Sparkles size={14} className={`sm:w-4 sm:h-4 ${activeTab === 'ai' ? 'text-teal-600' : 'text-slate-400'}`} />
                                    <span>Auto-Generate</span>
                                </button>
                                <button
                                    onClick={() => handleTabChange('manual')}
                                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer ${activeTab === 'manual'
                                        ? 'bg-white text-teal-700 shadow-md shadow-teal-500/5 translate-y-[-1px]'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-white/40'
                                        }`}
                                >
                                    <FileText size={14} className={`sm:w-4 sm:h-4 ${activeTab === 'manual' ? 'text-teal-600' : 'text-slate-400'}`} />
                                    <span>Manual Voucher</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Implementation */}
                    <AnimatePresence mode="wait">
                        {activeTab === 'ai' ? (
                            <motion.div
                                key="ai"
                                className="flex-1 flex flex-col justify-between min-h-0"
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -15 }}
                                transition={{ duration: 0.3 }}
                            >
                                <AiVoucherCreator
                                    initialFiles={[...preloadedHotelFiles, ...externalDroppedFiles]}
                                    initialHotelFiles={[...preloadedHotelFiles, ...externalDroppedFiles]}
                                    initialTravelFiles={preloadedTravelFiles}
                                    initialItineraryFiles={preloadedItineraryFiles}
                                    onSuccess={(data) => {
                                        const targetId = data?._id || 'review';
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                        navigate(`/voucher-edit/${targetId}`, {
                                            replace: true,
                                            state: {
                                                isReviewMode: true,
                                                fromAiExtraction: true,
                                                initialData: data,
                                                customTheme: currentVoucherTheme
                                            }
                                        });
                                    }}
                                    onSwitchToManual={() => handleTabChange('manual')}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="manual"
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -15 }}
                                transition={{ duration: 0.3 }}
                            >
                                <ManualVoucherForm
                                    initialData={manualInitialData}
                                    onDataChange={handleDataChange}
                                    customTheme={currentVoucherTheme}
                                    onSaved={() => {
                                        setIsFormSaved(true);
                                        setManualInitialData(null);
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </PageContainer>

                {/* Persistent Progress Modal across tabs */}
                <AiAnalysisProgress
                    analysisState={analysisState}
                    setAnalysisMinimized={setAnalysisMinimized}
                    resetGlobalAnalysis={resetAnalysis}
                />
            </div>

            {/* Exit Confirmation Modal */}
            {showExitConfirm && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setShowExitConfirm(false)}></div>
                    <div className="bg-white rounded-[2rem] p-7 max-w-sm w-full relative z-10 shadow-2xl animate-in zoom-in-95 duration-300 border border-slate-100 flex flex-col items-center">
                        <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mb-3.5 mx-auto ring-1 ring-amber-100 shadow-xs">
                            <FileText size={22} className="text-amber-500" />
                        </div>
                        <h3 className="text-xl font-medium text-slate-800 text-center mb-1.5 tracking-tight">Unsaved Changes</h3>
                        <p className="text-slate-500 text-center mb-6 leading-relaxed font-normal text-xs max-w-xs">
                            You have unsaved voucher details. Would you like to save them as a draft for later or discard them?
                        </p>
                        
                        {/* Side-by-side Action Buttons */}
                        <div className="grid grid-cols-2 gap-2.5 w-full mb-2.5">
                            <button
                                onClick={confirmDiscard}
                                className="w-full border border-rose-200 bg-rose-50/60 hover:bg-rose-100 text-rose-600 font-medium text-xs py-2.5 px-3 rounded-full transition-all flex items-center justify-center gap-1 active:scale-[0.98] cursor-pointer"
                            >
                                <span>Discard</span>
                            </button>
                            <button
                                onClick={confirmSaveDraft}
                                className="w-full bg-[#067978] hover:bg-[#056362] text-white font-medium text-xs py-2.5 px-3 rounded-full transition-all flex items-center justify-center gap-1 shadow-sm shadow-teal-900/10 active:scale-[0.98] cursor-pointer"
                            >
                                <span>Save Draft</span>
                                <ArrowRight size={13} />
                            </button>
                        </div>

                        {/* Cancel link */}
                        <button
                            onClick={() => setShowExitConfirm(false)}
                            className="text-slate-400 hover:text-slate-600 text-xs font-normal py-1 transition-colors cursor-pointer text-center"
                        >
                            Stay on Page
                        </button>
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default CreateVoucherPage;
